import { Button, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActions,
  DialogContent,
} from '@work4all/components/lib/dialog';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { DateTimeInputPicker } from '@work4all/components/lib/input/date-time-input-picker/DateTimeInputPicker';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input/LabeledInput';

import { useFormPlus, useUser } from '@work4all/data';
import { useUsersContext } from '@work4all/data/lib/hooks/users';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { ContractPickerField } from '../../../components/entity-picker/ContractPickerField';
import { PositionPickerField } from '../../../components/entity-picker/PositionPickerField';
import { UserPickerField } from '../../../components/entity-picker/UserPickerField';
import { ControllerPlus } from '../../../containers/mask-overlays/form-plus/controller-plus';
import { useFormUpdate } from '../../../containers/mask-overlays/mask-overlay/utils/use-form-update';

export interface PositionDoneFormDataType {
  contract?: {
    id: number;
    positionList: Position[];
  };
  position?: Position;
  user?: User;
  completionDate: string;
  notes: string;
  toRemoveDone?: boolean;
}

export type PositionDoneFormData = PathsOf<PositionDoneFormDataType, 2>;

interface PositionDoneDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: PositionDoneFormData) => void;
  contract?: Contract;
  overrides?: {
    user?: User;
    date?: string;
    position?: Position;
  };
}

export const PositionDoneDialog: React.FC<PositionDoneDialogProps> = ({
  open,
  onClose,
  onConfirm,
  contract,
  overrides,
}) => {
  const { t } = useTranslation();

  const currentUser = useUser();
  // This could be a resolver on BE, but this is just fine until we use users context
  const { usersById } = useUsersContext();

  const getDefaultValues = useCallback(
    (position: Position) => {
      let completionDate = overrides?.date
        ? DateTime.fromISO(overrides?.date).toFormat('yyyy-MM-dd')
        : new Date().toISOString().split('T')[0];
      let user: User = {
        id: currentUser?.benutzerCode,
        displayName: currentUser?.displayName,
      };
      if (position?.finishedByUserId) {
        user = usersById[position.finishedByUserId];
      }

      if (position?.finishedDate) {
        completionDate = DateTime.fromISO(position?.finishedDate).toFormat(
          'yyyy-MM-dd'
        );
      }
      return {
        contract: contract,
        position,
        user,
        completionDate,
        notes: position?.finishedNote ?? '',
      };
    },
    [contract, overrides, currentUser, usersById]
  );

  const defaultValues = useMemo(() => {
    const position = overrides?.position ?? contract?.positionList[0];
    return getDefaultValues(position);
  }, [contract, overrides, getDefaultValues]);

  const form = useFormPlus<PositionDoneFormData>({
    defaultValues,
  });

  const { control, handleSubmit, reset, register } = form;
  useFormUpdate(
    {
      contract: (contract: Contract) => {
        return getDefaultValues(contract?.positionList[0]);
      },
      position: (position: Position) => {
        return getDefaultValues(position);
      },
    },
    form
  );

  // Reset form values when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const onSubmit = (data: PositionDoneFormData) => {
    onConfirm(data);
    onClose();
  };

  const position = overrides?.position ?? contract?.positionList[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('POSITION_DONE_DIALOG.TITLE')}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack direction="column" spacing={2} sx={{ minWidth: 500 }}>
            {position?.isFinished && (
              <CheckboxRadioItem
                label={t('POSITION_DONE_DIALOG.CHECKBOX_REMOVE_DONE')}
                defaultChecked={false}
                {...register('toRemoveDone')}
              />
            )}
            <ControllerPlus
              name="contract"
              control={control}
              render={({ field }) => (
                <ContractPickerField
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <ControllerPlus
              name="position"
              control={control}
              render={({ field }) => (
                <PositionPickerField
                  value={field.value}
                  onChange={field.onChange}
                  positions={contract?.positionList ?? []}
                />
              )}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <ControllerPlus
                name="user"
                control={control}
                render={({ field }) => (
                  <UserPickerField
                    value={field.value}
                    onChange={field.onChange}
                    multiple={false}
                  />
                )}
              />

              <ControllerPlus
                name="completionDate"
                control={control}
                render={({ field }) => (
                  <DateTimeInputPicker
                    {...field}
                    withTime={false}
                    clearable={false}
                    dateLabel={t('POSITION_DONE_DIALOG.COMPLETION_DATE')}
                  />
                )}
              />
            </div>

            <ControllerPlus
              name="notes"
              control={control}
              render={({ field }) => (
                <LabeledInput
                  {...field}
                  label={t('COMMON.NOTES')}
                  multiline
                  minRows={5}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            {t('ALERTS.BTN_ABORT')}
          </Button>
          <Button variant="text" type="submit" color="primary">
            {t('POSITION_DONE_DIALOG.BUTTON_DONE')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
